// src/pages/jlptKanjiList.jsx
// Public JLPT-level kanji hub (SEO Phase 2a). Lists the kanji available for a
// JLPT level from bundled static data — no API, no login — so it prerenders to
// real HTML. Targets high-intent searches like "JLPT N5 kanji list".
import React from 'react';
import { usePostgresUser } from '../components/utils/PostgresUserContext';
import Navbar from '../components/navBar';
import Footer from '../components/footer';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import kanjiStatic from '../data/kanjiStatic.json';

const JLPT_BLURB = {
  N5: 'the beginner level — basic everyday kanji',
  N4: 'elementary kanji for common daily situations',
  N3: 'intermediate kanji bridging everyday and formal Japanese',
  N2: 'upper-intermediate kanji for newspapers and business',
  N1: 'advanced kanji for academic and specialized texts',
};

function JlptKanjiList() {
  const { level } = useParams();
  const { user, subscriptionStatus } = usePostgresUser();

  const jlpt = (level || '').toUpperCase();
  const kanjiList = kanjiStatic.byJlpt[jlpt] || [];
  const canonical = `https://onikanji.com/jlpt/${jlpt.toLowerCase()}`;

  if (!kanjiStatic.jlptLevels.includes(jlpt)) {
    return (
      <div className="font-noto-sans-jp">
        <Helmet>
          <title>JLPT Kanji | OniKanji</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Navbar user={user} subscriptionStatus={subscriptionStatus} />
        <div className="container mx-auto p-6 text-center mt-10">
          <h1 className="text-2xl font-bold text-logoColor">JLPT level not found</h1>
          <p className="mt-4">
            <Link to="/kanji" className="text-logoColor hover:underline">Browse all kanji</Link>
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  const title = `JLPT ${jlpt} Kanji List — ${kanjiList.length} Kanji on OniKanji`;
  const description = `The JLPT ${jlpt} kanji available on OniKanji (${kanjiList.length} of them) — ${JLPT_BLURB[jlpt]}. Tap any kanji for its meaning and readings. Free to browse.`;

  return (
    <div className="font-noto-sans-jp">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`JLPT ${jlpt} Kanji List | OniKanji`} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://onikanji.com/og-image.png" />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="OniKanji" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`JLPT ${jlpt} Kanji List | OniKanji`} />
        <meta name="twitter:image" content="https://onikanji.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'All Kanji', item: 'https://onikanji.com/kanji' },
            { '@type': 'ListItem', position: 2, name: `JLPT ${jlpt}`, item: canonical },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `JLPT ${jlpt} Kanji`,
          numberOfItems: kanjiList.length,
          itemListElement: kanjiList.map((k, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: `${k.c} (${k.m})`,
            url: `https://onikanji.com/kanji/${encodeURIComponent(k.c)}`,
          })),
        })}</script>
      </Helmet>

      <Navbar user={user} subscriptionStatus={subscriptionStatus} />

      <div className="container mx-auto space-y-4 p-6 max-sm:px-4 max-sm:py-4 sm:max-lg:!px-5 sm:max-lg:!py-4">
        <nav className="text-sm text-gray-500 mt-4 max-sm:mt-0">
          <Link to="/kanji" className="hover:underline">All Kanji</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700 font-medium">JLPT {jlpt}</span>
        </nav>

        <div className="flex flex-col items-center bg-lightBlueBackground text-lightBlueText p-4 max-sm:py-3 rounded-lg mt-4 max-sm:mt-2 sm:max-lg:!py-3">
          <h1 className="text-2xl max-sm:text-xl md:text-3xl font-bold">JLPT {jlpt} Kanji</h1>
          <div className="text-lg max-sm:text-base">{kanjiList.length} kanji available</div>
        </div>

        <p className="text-center text-gray-600 max-w-2xl mx-auto max-sm:text-sm">
          These are the JLPT {jlpt} kanji currently available on OniKanji — {JLPT_BLURB[jlpt]}.
          The curriculum is in active development, so more are added regularly.
        </p>

        <div className="flex flex-wrap justify-center gap-2 pt-2">
          {kanjiStatic.jlptLevels.map((j) => (
            <Link
              key={j}
              to={`/jlpt/${j.toLowerCase()}`}
              className={`px-3 py-1.5 max-sm:py-2.5 max-sm:min-w-[52px] max-sm:text-center rounded-lg font-semibold transition duration-300 ${
                j === jlpt ? 'bg-logoColor text-white' : 'bg-white border text-logoColor hover:bg-blue-100'
              }`}
            >
              {j}
            </Link>
          ))}
        </div>

        {kanjiList.length > 0 ? (
          <div className="grid grid-cols-2 max-sm:grid-cols-3 max-sm:gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:max-md:!grid-cols-5 md:max-lg:!grid-cols-6 sm:max-lg:!gap-3 mt-6">
            {kanjiList.map((k) => (
              <Link
                to={`/kanji/${k.c}`}
                key={k.c}
                className="flex flex-col items-center p-4 max-sm:px-1.5 max-sm:py-3 max-sm:min-w-0 sm:max-lg:!px-2 sm:max-lg:!py-3 sm:max-lg:min-w-0 bg-white border rounded-lg shadow hover:bg-blue-100 transition duration-300"
              >
                <div className="text-4xl max-sm:text-3xl max-sm:mb-1 font-bold mb-2">{k.c}</div>
                <div className="text-gray-600 text-center max-sm:text-xs max-sm:w-full max-sm:truncate">{k.m}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center p-8 bg-yellow-100 rounded-lg mt-6">
            <p className="text-xl font-semibold text-yellow-800">
              JLPT {jlpt} kanji are coming soon. Join the Discord for updates.
            </p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default JlptKanjiList;
